import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getSupabase } from "@/lib/supabase";

interface StatementData {
  dealer: {
    id: string;
    name: string;
    phone: string;
    address: string;
    balance: number;
    shareToken: string;
    parentDealerCode: string;
    parentDealerName: string;
    parentEmail: string;
  };
  ibans: Array<{ id: string; bank_name: string; iban_number: string }>;
  sales: Array<{
    id: string;
    amount: number;
    paymentMethod: string;
    date: string;
  }>;
  payments: Array<{
    id: string;
    amount: number;
    status: string;
    date: string;
    note: string;
  }>;
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;

export default function StatementPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<StatementData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showPayModal, setShowPayModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyAmount, setNotifyAmount] = useState("");
  const [notifyNote, setNotifyNote] = useState("");
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");
  const [salesOpen, setSalesOpen] = useState(true);
  const [paymentsOpen, setPaymentsOpen] = useState(true);

  useEffect(() => {
    if (!token) return;
    fetchStatement();
    fetch(`${SUPABASE_URL}/functions/v1/storage-setup`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string}`,
      },
    }).catch(() => {});
  }, [token]);

  const fetchStatement = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/dealer-statement`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string}`,
        },
        body: JSON.stringify({ shareToken: token }),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.message || "Veri yüklenemedi.");
      setData(result.data);
    } catch (err: any) {
      setError(err.message || "Link geçersiz veya süresi dolmuş.");
    } finally {
      setLoading(false);
    }
  };

  const copyIban = (iban: string) => {
    navigator.clipboard.writeText(iban).then(() => {
      setSubmitMsg("IBAN kopyalandı!");
      setTimeout(() => setSubmitMsg(""), 2000);
    });
  };

  const handleNotifySubmit = async () => {
    const amount = Number(notifyAmount);
    if (!amount || amount <= 0) {
      setSubmitMsg("Lütfen geçerli bir tutar girin.");
      return;
    }
    setSubmitting(true);
    setSubmitMsg("");
    try {
      let receiptUrl = "";
      if (receiptFile) {
        const supabase = getSupabase();
        const fileName = `${token}_${Date.now()}_${receiptFile.name}`;
        const { data: uploadData, error: uploadErr } = await supabase.storage
          .from("receipts")
          .upload(fileName, receiptFile, { upsert: true });
        if (uploadErr) throw new Error("Dekont yüklenirken hata: " + uploadErr.message);
        const { data: urlData } = supabase.storage.from("receipts").getPublicUrl(uploadData?.path || fileName);
        receiptUrl = urlData.publicUrl;
      }
      const res = await fetch(`${SUPABASE_URL}/functions/v1/submit-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string}`,
        },
        body: JSON.stringify({ shareToken: token, amount, receiptUrl, note: notifyNote }),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.message || "Bildirim gönderilemedi.");
      setSubmitMsg(result.message || "Ödeme bildiriminiz alındı!");
      setNotifyAmount("");
      setNotifyNote("");
      setReceiptFile(null);
      setTimeout(() => {
        setShowNotifyModal(false);
        setSubmitMsg("");
        fetchStatement();
      }, 2000);
    } catch (err: any) {
      setSubmitMsg(err.message || "Hata oluştu.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-foreground-400"></i>
          <p className="text-sm text-foreground-400 mt-2">Hesap ekstreniz yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-accent-600"></i>
          </div>
          <h4 className="text-lg font-semibold text-foreground-950 mb-1">Link Geçersiz</h4>
          <p className="text-sm text-foreground-500">{error || "Bu link artık kullanılamıyor."}</p>
        </div>
      </div>
    );
  }

  const { dealer, ibans, sales, payments } = data;
  const totalSales = sales.reduce((sum, s) => sum + s.amount, 0);
  const completedPayments = payments.filter((p) => p.status === "completed");
  const totalPayments = completedPayments.reduce((sum, p) => sum + p.amount, 0);
  const pendingPayments = payments.filter((p) => p.status === "pending");

  return (
    <div className="min-h-screen bg-background-50">
      {/* Header */}
      <div className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="shrink-0">
              <div className="w-20 h-20 rounded-full bg-secondary-100 border-4 border-background-50 flex items-center justify-center shadow-sm">
                <i className="ri-user-3-line text-3xl text-secondary-500"></i>
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-xl font-bold text-foreground-950">{dealer.name}</h1>
              <p className="text-sm text-foreground-500 mt-0.5">{dealer.parentDealerName} ({dealer.parentDealerCode})</p>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mt-3 text-sm">
                {dealer.phone && (
                  <span className="flex items-center gap-1.5 text-foreground-600">
                    <i className="ri-phone-line text-foreground-400"></i>{dealer.phone}
                  </span>
                )}
                {dealer.address && (
                  <span className="flex items-center gap-1.5 text-foreground-600">
                    <i className="ri-map-pin-line text-foreground-400"></i>{dealer.address}
                  </span>
                )}
                {dealer.parentEmail && (
                  <span className="flex items-center gap-1.5 text-foreground-600">
                    <i className="ri-mail-line text-foreground-400"></i>{dealer.parentEmail}
                  </span>
                )}
              </div>
            </div>
            <div className="shrink-0 bg-background-50 border border-background-200/70 rounded-xl px-6 py-4 text-center min-w-[180px]">
              <p className="text-xs text-foreground-400 mb-1">Güncel Bakiye</p>
              <p className={`text-2xl font-bold ${dealer.balance > 0 ? "text-accent-500" : dealer.balance < 0 ? "text-primary-600" : "text-foreground-700"}`}>
                ${dealer.balance.toLocaleString("en-US", { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-foreground-400 mt-1">{dealer.balance > 0 ? "Açık Bakiyesi" : dealer.balance < 0 ? "Alacaklı" : "Bakiyeniz yok"}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mt-6">
            <button onClick={() => setShowPayModal(true)} className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-bank-line"></i>Ödeme Yap
            </button>
            <button onClick={() => { setShowNotifyModal(true); setSubmitMsg(""); }} className="flex items-center gap-2 px-5 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-upload-cloud-2-line"></i>Ödeme Bildir
            </button>
          </div>
        </div>
      </div>

      {/* Tables */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Sales */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
            <button onClick={() => setSalesOpen(!salesOpen)} className="w-full flex items-center justify-between px-4 py-3 bg-secondary-500 text-background-50 hover:bg-secondary-600 transition-colors cursor-pointer">
              <span className="text-sm font-semibold">Önceki Satışlar</span>
              <i className={`ri-arrow-up-s-line text-lg transition-transform ${salesOpen ? "" : "rotate-180"}`}></i>
            </button>
            {salesOpen && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-100 text-left">
                      <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Tarih</th>
                      <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Durum</th>
                      <th className="px-4 py-2.5 text-xs font-medium text-foreground-500 text-right">Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.length === 0 ? (
                      <tr><td colSpan={3} className="px-4 py-6 text-center text-sm text-foreground-400">Henüz satış kaydı bulunmuyor.</td></tr>
                    ) : (
                      sales.map((sale) => (
                        <tr key={sale.id} className="border-t border-background-200/70 hover:bg-background-100/40 transition-colors">
                          <td className="px-4 py-3 text-foreground-700">{new Date(sale.date).toLocaleDateString("tr-TR")}</td>
                          <td className="px-4 py-3"><span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700">Faturalaşmış</span></td>
                          <td className="px-4 py-3 text-right font-medium text-foreground-900">${sale.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {sales.length > 0 && (
                    <tfoot>
                      <tr className="border-t border-background-200/70 bg-background-100">
                        <td colSpan={2} className="px-4 py-2.5 text-xs font-medium text-foreground-500">Toplam</td>
                        <td className="px-4 py-2.5 text-right text-xs font-bold text-foreground-900">${totalSales.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </div>

          {/* Payments */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
            <button onClick={() => setPaymentsOpen(!paymentsOpen)} className="w-full flex items-center justify-between px-4 py-3 bg-secondary-500 text-background-50 hover:bg-secondary-600 transition-colors cursor-pointer">
              <span className="text-sm font-semibold">Önceki Ödemeler</span>
              <i className={`ri-arrow-up-s-line text-lg transition-transform ${paymentsOpen ? "" : "rotate-180"}`}></i>
            </button>
            {paymentsOpen && (
              <div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-background-100 text-left">
                        <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Tarih</th>
                        <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Durum</th>
                        <th className="px-4 py-2.5 text-xs font-medium text-foreground-500 text-right">Tutar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.length === 0 ? (
                        <tr><td colSpan={3} className="px-4 py-6 text-center text-sm text-foreground-400">Henüz ödeme kaydı bulunmuyor.</td></tr>
                      ) : (
                        payments.map((payment) => (
                          <tr key={payment.id} className="border-t border-background-200/70 hover:bg-background-100/40 transition-colors">
                            <td className="px-4 py-3 text-foreground-700">{new Date(payment.date).toLocaleDateString("tr-TR")}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${payment.status === "completed" ? "bg-primary-100 text-primary-700" : "bg-accent-100 text-accent-700"}`}>
                                {payment.status === "completed" ? "Onaylandı" : "Beklemede"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-foreground-900">${payment.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {completedPayments.length > 0 && (
                      <tfoot>
                        <tr className="border-t border-background-200/70 bg-background-100">
                          <td colSpan={2} className="px-4 py-2.5 text-xs font-medium text-foreground-500">Toplam</td>
                          <td className="px-4 py-2.5 text-right text-xs font-bold text-foreground-900">${totalPayments.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                {pendingPayments.length > 0 && (
                  <div className="px-4 py-3 bg-accent-50 border-t border-accent-100">
                    <p className="text-xs text-accent-700 flex items-center gap-1.5"><i className="ri-time-line"></i>{pendingPayments.length} ödeme bildiriminiz onay bekliyor.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pay Modal */}
      {showPayModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" onClick={() => setShowPayModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-foreground-950">Banka Hesapları</h4>
              <button onClick={() => setShowPayModal(false)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <p className="text-sm text-foreground-500 mb-4">Aşağıdaki IBAN numaralarından birine havale/EFT yapabilirsiniz. Ödemenizi yaptıktan sonra "Ödeme Bildir" butonu ile dekontunuzu yükleyiniz.</p>
            {ibans.length === 0 ? (
              <div className="text-center py-6 text-sm text-foreground-400"><i className="ri-bank-line text-2xl block mb-2"></i>Henüz tanımlı banka hesabı bulunmuyor.</div>
            ) : (
              <div className="space-y-2">
                {ibans.map((iban) => (
                  <div key={iban.id} className="bg-background-100 rounded-lg p-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-900">{iban.bank_name}</p>
                      <p className="text-xs text-foreground-400 mt-0.5 font-mono">{iban.iban_number}</p>
                    </div>
                    <button onClick={() => copyIban(iban.iban_number)} className="shrink-0 px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"><i className="ri-file-copy-line mr-1"></i>Kopyala</button>
                  </div>
                ))}
              </div>
            )}
            {submitMsg && <div className="mt-3 bg-primary-100 text-primary-700 text-xs rounded-lg px-3 py-2 text-center">{submitMsg}</div>}
          </div>
        </div>
      )}

      {/* Notify Modal */}
      {showNotifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" onClick={() => setShowNotifyModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-foreground-950">Ödeme Bildir</h4>
              <button onClick={() => setShowNotifyModal(false)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Ödeme Tutarı ($) *</label>
                <div className="relative">
                  <input type="number" min="0.01" step="0.01" value={notifyAmount} onChange={(e) => setNotifyAmount(e.target.value)} placeholder="0,00" className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400">$</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Dekont / Ekran Görüntüsü</label>
                <input type="file" accept="image/*,.pdf" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 file:mr-3 file:px-3 file:py-1 file:bg-primary-500 file:text-background-50 file:rounded-md file:border-0 file:text-xs file:font-medium cursor-pointer" />
                {receiptFile && <p className="text-xs text-foreground-500 mt-1">Seçilen: {receiptFile.name}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Not (İsteğe Bağlı)</label>
                <textarea value={notifyNote} onChange={(e) => setNotifyNote(e.target.value)} maxLength={500} rows={2} placeholder="Eklemek istediğiniz not..." className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none" />
                <p className="text-xs text-foreground-400 mt-0.5 text-right">{notifyNote.length}/500</p>
              </div>
            </div>
            {submitMsg && (
              <div className={`mt-3 text-xs rounded-lg px-3 py-2 text-center ${submitMsg.includes("aldı") || submitMsg.includes("başar") ? "bg-primary-100 text-primary-700" : "bg-accent-100 text-accent-700"}`}>{submitMsg}</div>
            )}
            <button onClick={handleNotifySubmit} disabled={submitting || !notifyAmount} className="w-full mt-4 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
              {submitting ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin"></i>Gönderiliyor...</span> : <span className="flex items-center justify-center gap-2"><i className="ri-send-plane-line"></i>Bildirimi Gönder</span>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}